import React, { useCallback } from "react";
import MapView from "./MapView";
import MyShopsModal from "./MyShopsModal";
import ShopListContent from "./ShopListContent";
import { Shop } from "../types/Shop";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "./ui/drawer";
import { cn } from "@/lib/utils";

interface MainContentProps {
  showListModal: boolean;
  showMyShopsModal: boolean;
  filteredShops: Shop[];
  userLocation: [number, number] | null;
  isDarkMode: boolean;
  isTrackingLocation: boolean;
  defaultMapType: string;
  closeUserMenu: (() => void) | null;
  myShops: Shop[];
  handleShopDeleted: (deletedShopId: string) => void;
  handleShopUpdated: () => void;
  onCloseListModal: () => void;
  onCloseMyShopsModal: () => void;
  showFilters: boolean;
  // For adjusting map when NavPanel is open on desktop
  navPanelOpen?: boolean;
}

const MainContent: React.FC<MainContentProps> = ({
  showListModal,
  showMyShopsModal,
  filteredShops,
  userLocation,
  isDarkMode,
  isTrackingLocation,
  defaultMapType,
  closeUserMenu,
  myShops,
  handleShopDeleted,
  handleShopUpdated,
  onCloseListModal,
  onCloseMyShopsModal,
  showFilters,
  navPanelOpen = false,
}) => {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isTablet = useMediaQuery("(min-width: 768px)");
  const isMobile = !isTablet;

  // Stable callback to prevent MapView re-renders
  const handleMapClick = useCallback(() => {
    if (closeUserMenu) {
      closeUserMenu();
    }
  }, [closeUserMenu]);

  // Shop list content - reused in both panel and drawer
  const shopListContent = (
    <ShopListContent
      shops={filteredShops}
      userLocation={userLocation}
      isDarkMode={isDarkMode}
    />
  );

  return (
    <>
      {/* MapView - Adjusts for NavPanel on desktop */}
      <main
        className={cn(
          "h-full relative overflow-hidden transition-all duration-300 ease-in-out",
          // On desktop, shift and resize when NavPanel is open (not collapsed)
          isDesktop && navPanelOpen && "ml-[400px]"
        )}
        style={{
          width: isDesktop && navPanelOpen ? "calc(100% - 400px)" : "100%",
        }}
      >
        <MapView
          shops={filteredShops}
          userLocation={userLocation}
          isDarkMode={isDarkMode}
          isTrackingLocation={isTrackingLocation}
          defaultMapType={defaultMapType}
          onMapClick={handleMapClick}
        />
      </main>

      {/* Mobile ONLY: Bottom Drawer for shop list */}
      {/* On tablet/desktop, NavPanel in App.tsx handles the shop list */}
      {isMobile && (
        <Drawer open={showListModal} onOpenChange={(open) => !open && onCloseListModal()}>
          <DrawerContent className={cn(
            "max-h-[75vh]",
            isDarkMode ? "bg-gray-900 border-gray-800" : "bg-white"
          )}>
            <DrawerHeader className={cn(
              "border-b",
              isDarkMode ? "border-gray-800" : "border-gray-200"
            )}>
              <DrawerTitle className={isDarkMode ? "text-white" : ""}>
                Shops ({filteredShops.length})
              </DrawerTitle>
            </DrawerHeader>
            <div className="flex-1 overflow-y-auto">
              {shopListContent}
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {/* My Shops Modal - Keep as modal for now */}
      <MyShopsModal
        isOpen={showMyShopsModal}
        onClose={onCloseMyShopsModal}
        shops={myShops}
        isDarkMode={isDarkMode}
        onShopDeleted={handleShopDeleted}
        onShopUpdated={handleShopUpdated}
        showFilters={showFilters}
      />
    </>
  );
};

export default MainContent;
